import React, { useRef, useState } from 'react';
import { Menu, MenuTrigger, MenuPopover } from '@fluentui/react-components';
import { ChevronDown12Regular } from '@fluentui/react-icons';
import {
    DURATION_SUFFIX_HELP,
    DURATION_UNITS,
    draftHasNumber,
    formatDuration,
    getDurationAmountInUnit,
    inferDurationEditState,
    isSaveableDurationMinutes,
    parseDurationAmountWithUnit,
    parseDurationWithDefaultUnit,
} from '../../utils/taskSchedulingUtils';
import styles from './css/index.module.css';

const DEFAULT_UNIT = 'm';

const DurationPickerMenu = ({
    durationMinutes,
    durationLabel,
    readOnly = false,
    readOnlyTitle,
    placeholder = '—',
    onSave,
    onEditingChange,
    children,
}) => {
    const inputRef = useRef(null);
    const suppressCloseRef = useRef(false);
    const [open, setOpen] = useState(false);
    const [unitMenuOpen, setUnitMenuOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [selectedUnit, setSelectedUnit] = useState(DEFAULT_UNIT);

    const displayValue =
        durationMinutes != null && durationMinutes !== ''
            ? formatDuration(durationMinutes)
            : durationLabel || '';

    const seedDraftFromLabel = () => {
        if (!durationLabel) return '';
        const dayMatch = durationLabel.match(/^(\d+(?:\.\d+)?)\s+days?$/i);
        if (dayMatch) return dayMatch[1];
        return durationLabel.replace(/[^\d.]/g, '') || '';
    };

    const resetEditorState = () => {
        setUnitMenuOpen(false);
        if (durationMinutes != null && durationMinutes !== '') {
            const { unit, amount } = inferDurationEditState(Number(durationMinutes), DEFAULT_UNIT);
            setSelectedUnit(unit);
            setDraft(amount);
            return;
        }
        setSelectedUnit(DEFAULT_UNIT);
        setDraft(seedDraftFromLabel());
    };

    const getDraftNumericAmount = () => {
        const text = String(draft || '').trim();
        if (draftHasNumber(text)) {
            const match = text.match(/^(\d+(?:\.\d+)?)/);
            return match ? Number(match[1]) : null;
        }
        if (durationMinutes != null && durationMinutes !== '') {
            return getDurationAmountInUnit(Number(durationMinutes), selectedUnit);
        }
        return null;
    };

    const saveMinutes = async parsed => {
        if (parsed == null || !onSave || !isSaveableDurationMinutes(parsed)) return;
        await onSave(parsed);
    };

    const commitDraft = async () => {
        if (!draftHasNumber(draft)) return;
        const parsed = parseDurationWithDefaultUnit(draft, selectedUnit);
        if (parsed == null) return;
        await saveMinutes(parsed);
    };

    const closeEditor = () => {
        setDraft('');
        setUnitMenuOpen(false);
        setOpen(false);
        onEditingChange?.(false);
    };

    const handleOpenChange = async (e, data) => {
        if (!data.open && suppressCloseRef.current) {
            setOpen(true);
            return;
        }

        if (data.open) {
            resetEditorState();
            setOpen(true);
            onEditingChange?.(true);
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
            return;
        }

        if (draftHasNumber(draft)) {
            await commitDraft();
        }
        closeEditor();
    };

    const handleUnitSelect = async unit => {
        if (readOnly) return;
        if (unit === selectedUnit) {
            setUnitMenuOpen(false);
            return;
        }

        suppressCloseRef.current = true;

        const amount = getDraftNumericAmount();
        setSelectedUnit(unit);
        setUnitMenuOpen(false);

        if (amount == null) {
            suppressCloseRef.current = false;
            return;
        }

        const parsed = parseDurationAmountWithUnit(amount, unit);
        if (parsed == null) {
            suppressCloseRef.current = false;
            return;
        }

        const nextDraft = inferDurationEditState(parsed, unit);
        setDraft(nextDraft.amount);

        try {
            await saveMinutes(parsed);
        } finally {
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                suppressCloseRef.current = false;
            });
        }
    };

    const handleKeyDown = e => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            e.preventDefault();
            commitDraft().then(closeEditor);
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            if (unitMenuOpen) {
                setUnitMenuOpen(false);
                return;
            }
            closeEditor();
        }
    };

    const stopRowDrag = e => e.stopPropagation();

    const selectedUnitMeta = DURATION_UNITS.find(option => option.value === selectedUnit) || DURATION_UNITS[0];

    if (readOnly) {
        return (
            <div className={styles.durationCell} title={readOnlyTitle} style={{ cursor: 'default' }}>
                <span className={styles.tableRegularText} style={{ whiteSpace: 'nowrap' }}>
                    {displayValue || '—'}
                </span>
            </div>
        );
    }

    const triggerContent =
        children ??
        (
            <button
                type="button"
                className={styles.durationTrigger}
                aria-label="Edit duration"
                onPointerDown={stopRowDrag}
                onMouseDown={stopRowDrag}
                onClick={stopRowDrag}>
                <span className={styles.tableRegularText} style={{ whiteSpace: 'nowrap' }}>
                    {displayValue || placeholder}
                </span>
            </button>
        );

    return (
        <div
            className={styles.durationCell}
            title={DURATION_SUFFIX_HELP}
            onPointerDown={stopRowDrag}
            onMouseDown={stopRowDrag}
            onClick={stopRowDrag}>
            <Menu open={open} onOpenChange={handleOpenChange}>
                <MenuTrigger disableButtonEnhancement>{triggerContent}</MenuTrigger>
                <MenuPopover
                    style={{ minWidth: 220, overflow: 'visible', padding: 0 }}
                    onPointerDown={stopRowDrag}
                    onMouseDown={stopRowDrag}
                    onClick={stopRowDrag}>
                    <div className={styles.durationPopover}>
                        <div className={styles.durationPopoverField}>
                            <input
                                ref={inputRef}
                                type="text"
                                inputMode="decimal"
                                value={draft}
                                placeholder="e.g. 30"
                                onChange={e => setDraft(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className={styles.durationPopoverInput}
                                aria-label="Duration"
                            />
                            <button
                                type="button"
                                className={`${styles.durationUnitTrigger} ${unitMenuOpen ? styles.durationUnitTriggerOpen : ''}`}
                                aria-label={`Duration unit: ${selectedUnitMeta.description}`}
                                aria-expanded={unitMenuOpen}
                                onPointerDown={stopRowDrag}
                                onMouseDown={stopRowDrag}
                                onClick={e => {
                                    stopRowDrag(e);
                                    setUnitMenuOpen(current => !current);
                                }}>
                                <span className={styles.durationUnitLabel}>{selectedUnitMeta.label}</span>
                                <ChevronDown12Regular className={unitMenuOpen ? styles.durationUnitChevronOpen : undefined} />
                            </button>
                        </div>
                        {unitMenuOpen ? (
                            <div className={styles.durationUnitMenuInline} role="listbox" aria-label="Duration unit">
                                {DURATION_UNITS.map(option => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        role="option"
                                        aria-selected={option.value === selectedUnit}
                                        className={
                                            option.value === selectedUnit ? styles.durationUnitMenuItemActive : styles.durationUnitMenuItem
                                        }
                                        onPointerDown={e => {
                                            e.preventDefault();
                                            stopRowDrag(e);
                                        }}
                                        onMouseDown={e => {
                                            e.preventDefault();
                                            stopRowDrag(e);
                                        }}
                                        onClick={e => {
                                            e.preventDefault();
                                            stopRowDrag(e);
                                            handleUnitSelect(option.value);
                                        }}>
                                        <span className={styles.durationUnitMenuLabel}>{option.label}</span>
                                        <span className={styles.durationUnitMenuDescription}>= {option.description}</span>
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </MenuPopover>
            </Menu>
        </div>
    );
};

export default DurationPickerMenu;
